import React from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

export type TimeRange = "1M" | "3M" | "6M" | "ALL";

const RANGE_DAYS: Record<Exclude<TimeRange, "ALL">, number> = { "1M": 30, "3M": 91, "6M": 182 };

/** Keeps the points inside the range (always at least two so a line can be drawn). */
export function sliceByRange<T>(data: T[], range: TimeRange, dateOf: (d: T) => number): T[] {
  if (range === "ALL" || data.length < 3) return data;
  const from = Date.now() - RANGE_DAYS[range] * 86_400_000;
  const idx = data.findIndex((d) => dateOf(d) >= from);
  if (idx <= 0) return data;
  return data.slice(Math.min(idx - 1, data.length - 2));
}

/** Ranges worth offering for a series that starts at `firstTs`. */
export const availableRanges = (firstTs?: number): TimeRange[] => {
  if (!firstTs) return ["ALL"];
  const days = (Date.now() - firstTs) / 86_400_000;
  const short: TimeRange[] = (["1M", "3M", "6M"] as const).filter((r) => days > RANGE_DAYS[r] * 1.15);
  return [...short, "ALL"];
};

/** Compact glass segmented control for chart time windows. */
export const TimeRangeToggle: React.FC<{
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  options?: TimeRange[];
}> = ({ value, onChange, options = ["1M", "3M", "6M", "ALL"] }) => {
  if (options.length < 2) return null;
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, v: TimeRange | null) => v && onChange(v)}
      aria-label="Time range"
      sx={{
        "& .MuiToggleButton-root": {
          px: { xs: 1, md: 1.25 },
          py: 0.375,
          minWidth: { xs: 32, md: 38 },
          fontSize: "0.75rem",
          fontWeight: 650,
          "@media (pointer: coarse)": { py: 0.75, minWidth: 40 },
        },
      }}
    >
      {options.map((r) => (
        <ToggleButton key={r} value={r} aria-label={r === "ALL" ? "All time" : r}>
          {r === "ALL" ? "All" : r}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default TimeRangeToggle;
